/**
 * Location Store
 * Zustand store for location data (countries, states, cities) state management
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { CountryInfo, StateInfo, CityInfo } from '@/types/api/location';

/**
 * Location store state
 */
export interface LocationState {
  // Data
  countries: CountryInfo[];
  statesByCountry: Record<string, StateInfo[]>; // Keyed by countryCode
  citiesByState: Record<number, CityInfo[]>; // Keyed by stateId

  // UI State
  isLoading: boolean;
  error: string | null;

  // Actions - Data
  setCountries: (countries: CountryInfo[]) => void;
  setStatesForCountry: (countryCode: string, states: StateInfo[]) => void;
  setCitiesForState: (stateId: number, cities: CityInfo[]) => void;

  // Actions - UI State
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;

  // Actions - Reset
  reset: () => void;
  clearStates: () => void;
  clearCities: () => void;
}

/**
 * Initial state values
 */
const initialState = {
  countries: [],
  statesByCountry: {},
  citiesByState: {},
  isLoading: false,
  error: null,
};

/**
 * Location Zustand store
 * Persists countries, states, and cities data as it rarely changes
 */
export const useLocationStore = create<LocationState>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,

        // Data actions
        setCountries: (countries) =>
          set({ countries }, false, 'location/setCountries'),

        setStatesForCountry: (countryCode, states) =>
          set(
            (state) => ({
              statesByCountry: {
                ...state.statesByCountry,
                [countryCode]: states,
              },
            }),
            false,
            'location/setStatesForCountry'
          ),

        setCitiesForState: (stateId, cities) =>
          set(
            (state) => ({
              citiesByState: {
                ...state.citiesByState,
                [stateId]: cities,
              },
            }),
            false,
            'location/setCitiesForState'
          ),

        // UI state actions
        setLoading: (isLoading) =>
          set({ isLoading }, false, 'location/setLoading'),

        setError: (error) =>
          set({ error }, false, 'location/setError'),

        // Reset actions
        reset: () =>
          set(initialState, false, 'location/reset'),

        clearStates: () =>
          set({ statesByCountry: {} }, false, 'location/clearStates'),

        clearCities: () =>
          set({ citiesByState: {} }, false, 'location/clearCities'),
      }),
      {
        name: 'location-storage',
        // Persist all location data as it's reference data
        partialize: (state) => ({
          countries: state.countries,
          statesByCountry: state.statesByCountry,
          citiesByState: state.citiesByState,
        }),
      }
    ),
    { name: 'LocationStore' }
  )
);

/**
 * Selectors for common queries
 */

// Get all countries
export const useCountries = () => {
  return useLocationStore((state) => state.countries);
};

// Empty arrays as constants to avoid re-renders
const EMPTY_STATES: StateInfo[] = [];
const EMPTY_CITIES: CityInfo[] = [];

// Get states for a specific country code
export const useStatesForCountry = (countryCode: string | null) => {
  return useLocationStore((state) => {
    if (!countryCode) return EMPTY_STATES;
    return state.statesByCountry[countryCode] || EMPTY_STATES;
  });
};

// Get cities for a specific state
export const useCitiesForState = (stateId: number | null) => {
  return useLocationStore((state) => {
    if (!stateId) return EMPTY_CITIES;
    return state.citiesByState[stateId] || EMPTY_CITIES;
  });
};

// Get country by ID
export const useCountryById = (countryId: number | null) => {
  return useLocationStore((state) =>
    countryId
      ? state.countries.find((c) => c.countryId === countryId)
      : undefined
  );
};

// Get country by code
export const useCountryByCode = (countryCode: string | null) => {
  return useLocationStore((state) =>
    countryCode
      ? state.countries.find((c) => c.countryCode === countryCode)
      : undefined
  );
};

// Get state by ID
export const useStateById = (stateId: number | null) => {
  return useLocationStore((state) => {
    if (!stateId) return undefined;

    // Search through all state arrays
    for (const states of Object.values(state.statesByCountry)) {
      const found = states.find((s) => s.stateId === stateId);
      if (found) return found;
    }
    return undefined;
  });
};

// Get city by ID
export const useCityById = (cityId: number | null) => {
  return useLocationStore((state) => {
    if (!cityId) return undefined;

    // Search through all city arrays
    for (const cities of Object.values(state.citiesByState)) {
      const found = cities.find((c) => c.cityId === cityId);
      if (found) return found;
    }
    return undefined;
  });
};

// Check if location data is loaded
export const useHasLocationData = () => {
  return useLocationStore((state) => state.countries.length > 0);
};
